import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, User, CalendarClock, Target, Loader2 } from "lucide-react";
import PlaceSearch from "./PlaceSearch";

const PURPOSES = [
  "Career", "Marriage", "Love & Relationships", "Education", "Finance",
  "Health & Well-being", "Family", "Spiritual Growth", "General Life Reading",
];

const inputCls =
  "w-full rounded-xl border border-tara-teal/25 bg-white/85 px-4 py-3 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-teal/60 transition-colors";

const STEPS = [
  { icon: User, label: "You" },
  { icon: CalendarClock, label: "Birth details" },
  { icon: Target, label: "Guidance" },
];

export const emptyBirth = {
  name: "", gender: "", date: "", time: "", place: "",
  latitude: null, longitude: null, purposes: [],
};

// Premium 3-step birth details form. `steps={2}` hides the purpose step (used by matching).
export default function BirthForm({ value, onChange, onSubmit, loading, steps = 3, idPrefix = "birth", cta = "Generate my Kundali" }) {
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const set = (k, v) => onChange({ ...value, [k]: v });

  const validate = (s) => {
    const e = {};
    if (s === 0 && value.name.trim().length < 2) e.name = "Please enter your full name";
    if (s === 1) {
      if (!value.date) e.date = "Date of birth is required";
      if (!value.time) e.time = "Exact time of birth is required";
      if (!value.latitude) e.place = "Choose your birth place from the suggestions";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep((s) => Math.min(s + 1, steps - 1)); };
  const submit = () => { if (validate(0) && validate(1)) onSubmit(); };

  const togglePurpose = (p) =>
    set("purposes", value.purposes.includes(p)
      ? value.purposes.filter((x) => x !== p)
      : [...value.purposes, p]);

  return (
    <div className="glass-liquid rounded-[1.8rem] p-7 sm:p-9" data-testid={`${idPrefix}-form`}>
      {/* progress */}
      <div className="mb-8 flex items-center gap-3">
        {STEPS.slice(0, steps).map((s, i) => (
          <React.Fragment key={s.label}>
            <button
              onClick={() => i < step && setStep(i)}
              className="flex items-center gap-2.5"
              data-testid={`${idPrefix}-step-${i}`}
            >
              <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold transition-all ${
                i <= step ? "bg-gradient-to-br from-tara-teal to-tara-vermilion text-white" : "border border-tara-teal/30 text-tara-muted"
              }`}>
                <s.icon className="h-4 w-4" />
              </span>
              <span className={`hidden sm:block text-[11px] uppercase tracking-[0.16em] ${i <= step ? "text-tara-ink" : "text-tara-muted"}`}>
                {s.label}
              </span>
            </button>
            {i < steps - 1 && (
              <span className="h-[2px] flex-1 rounded-full bg-tara-sand overflow-hidden">
                <span className="block h-full rounded-full bg-gradient-to-r from-tara-teal to-tara-vermilion transition-all duration-500"
                      style={{ width: i < step ? "100%" : "0%" }} />
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-4"
        >
          {step === 0 && (
            <>
              <label className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">Full name *</span>
                <input value={value.name} onChange={(e) => set("name", e.target.value)} className={inputCls}
                       placeholder="Your full name" data-testid={`${idPrefix}-name-input`} />
                {errors.name && <span className="mt-1 block text-xs text-red-700" data-testid={`${idPrefix}-name-error`}>{errors.name}</span>}
              </label>
              <div>
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">Gender (optional)</span>
                <div className="flex flex-wrap gap-2">
                  {["Male", "Female", "Prefer not to say"].map((g) => (
                    <button key={g} type="button" onClick={() => set("gender", g)}
                            className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${
                              value.gender === g
                                ? "bg-gradient-to-r from-tara-teal to-tara-vermilion text-white"
                                : "border border-tara-teal/25 text-tara-body hover:border-tara-teal/50"
                            }`}
                            data-testid={`${idPrefix}-gender-${g.split(" ")[0].toLowerCase()}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">Date of birth *</span>
                  <input type="date" value={value.date} onChange={(e) => set("date", e.target.value)}
                         className={inputCls} data-testid={`${idPrefix}-date-input`} />
                  {errors.date && <span className="mt-1 block text-xs text-red-700">{errors.date}</span>}
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">Exact time of birth *</span>
                  <input type="time" value={value.time} onChange={(e) => set("time", e.target.value)}
                         className={inputCls} data-testid={`${idPrefix}-time-input`} />
                  {errors.time && <span className="mt-1 block text-xs text-red-700">{errors.time}</span>}
                </label>
              </div>
              <div>
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">Birth place *</span>
                <PlaceSearch
                  value={value.place}
                  testid={`${idPrefix}-place-input`}
                  onSelect={(p) => onChange({ ...value, place: p.label, latitude: p.latitude, longitude: p.longitude })}
                />
                {errors.place && <span className="mt-1 block text-xs text-red-700">{errors.place}</span>}
                <p className="mt-2 text-[11px] text-tara-muted">
                  The closer the birth time, the more precise the Lagna and Nakshatra calculation.
                </p>
              </div>
            </>
          )}

          {step === 2 && (
            <div>
              <span className="mb-3 block text-[11px] uppercase tracking-[0.16em] text-tara-muted">
                What would you like guidance about? (choose any)
              </span>
              <div className="flex flex-wrap gap-2.5">
                {PURPOSES.map((p) => (
                  <button key={p} type="button" onClick={() => togglePurpose(p)}
                          className={`rounded-full px-4 py-2.5 text-xs font-semibold transition-all ${
                            value.purposes.includes(p)
                              ? "bg-gradient-to-r from-tara-teal to-tara-vermilion text-white"
                              : "border border-tara-teal/25 bg-white/70 text-tara-body hover:border-tara-teal/50"
                          }`}
                          data-testid={`${idPrefix}-purpose-${p.split(" ")[0].toLowerCase()}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between gap-4">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="btn-ghost-gold flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-40"
          data-testid={`${idPrefix}-back-btn`}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < steps - 1 ? (
          <button onClick={next} className="btn-sacred flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-serif font-semibold" data-testid={`${idPrefix}-next-btn`}>
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={loading} className="btn-sacred aura-pulse flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-serif font-semibold" data-testid={`${idPrefix}-submit-btn`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {cta}
          </button>
        )}
      </div>
    </div>
  );
}
